import * as z from 'zod';
import { createSelectSchema } from 'drizzle-zod';

import { ABOUT_MAX_LENGTH, MEMBER_NAME_MAX_LENGTH, MEMBER_NAME_DENY_PATTERN } from '@iichi/domain/member/constants';
import type { QueryResult } from '@iichi/schema-database/types/member';
import * as schema from '@iichi/schema-database';

import { emailSchema } from '../lib/email';

const memberI18NSchema = createSelectSchema(schema.memberI18N)
  .pick({
    memberName: true,
    about: true,
  })
  .extend({
    memberName: z
      .string()
      .min(1)
      .max(MEMBER_NAME_MAX_LENGTH)
      .refine((value: string) => MEMBER_NAME_DENY_PATTERN.test(value) === false, '使用できないフレーズが含まれています'),
    about: z.string().max(ABOUT_MAX_LENGTH),
  });

export const memberSchema = createSelectSchema(schema.member)
  .pick({
    memberId: true,
    slug: true,
    alias: true,
    email: true,
    password: true,
    emailConfirmed: true,
    emailBounceCount: true,
    emailSuspended: true,
    likeCount: true,
    deleted: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    // 型の調整
    email: emailSchema,
    password: z.hash('md5'),
    emailConfirmed: z.coerce.boolean(),
    emailSuspended: z.coerce.boolean(),
    emailBounceCount: z.coerce.number(),
    likeCount: z.coerce.number(),
    deleted: z.coerce.boolean(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  // プロパティを追加
  .extend({
    aliasOrSlug: z.string(),
  })
  // member_i18nのフィールドを追加
  .extend({
    memberName: memberI18NSchema.shape.memberName,
    about: memberI18NSchema.shape.about,
  });

export type Member = z.infer<typeof memberSchema>;

export const transformMember = (data: QueryResult): Member => {
  const member = {
    memberId: data.memberId,
    slug: data.slug,
    alias: data.alias,
    aliasOrSlug: data.alias || data.slug,
    memberName: data.memberI18Ns?.[0]?.memberName ?? '',
    about: data.memberI18Ns?.[0]?.about ?? '',
    email: data.email,
    emailConfirmed: data.emailConfirmed,
    emailBounceCount: data.emailBounceCount,
    emailSuspended: data.emailSuspended,
    likeCount: data.likeCount,
    deleted: data.deleted,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };

  return memberSchema.parse(member);
};
